<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useLocale } from 'shared/composables/useLocale';

const { t } = useI18n();
const { resolvedLocale } = useLocale();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');

const dialogRef = ref(null);
const scheduledAt = ref('');
const content = ref('');
const selectedTemplate = ref(null);
const initialProcessedParams = ref(undefined);
const templateParserRef = ref(null);
const isSubmitting = ref(false);
const editingMessageId = ref(null);
const editingLockVersion = ref(null);
const clock = ref(Date.now());
const windowLoadedAt = ref(Date.now());
let clockInterval;

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const messageWindow = computed(
  () => currentChat.value?.scheduled_message_window || {}
);
const scheduledMessage = computed(
  () => currentChat.value?.scheduled_message || null
);
const conversationId = computed(() => currentChat.value?.id);
const inboxId = computed(() => currentChat.value?.inbox_id);
const availableTemplates = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  inboxId
);
const isEditing = computed(() => editingMessageId.value !== null);
const isSavedTemplateUnavailable = computed(
  () =>
    isEditing.value &&
    scheduledMessage.value?.content_type === 'template' &&
    !selectedTemplate.value
);

const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolvedLocale.value, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
    })
);

const formattedWindowClose = computed(() => {
  const closesAt = messageWindow.value.closes_at;
  return closesAt ? dateFormatter.value.format(new Date(closesAt)) : '';
});

const remainingTime = computed(() => {
  const serverRemainingSeconds = Number(messageWindow.value.remaining_seconds);
  if (!Number.isFinite(serverRemainingSeconds)) {
    return { hours: 0, minutes: 0 };
  }

  const elapsedSeconds = Math.max(
    Math.floor((clock.value - windowLoadedAt.value) / 1000),
    0
  );
  const remainingMinutes = Math.max(
    Math.floor((serverRemainingSeconds - elapsedSeconds) / 60),
    0
  );
  return {
    hours: Math.floor(remainingMinutes / 60),
    minutes: remainingMinutes % 60,
  };
});

const scheduledAtTimestamp = computed(() =>
  new Date(scheduledAt.value).getTime()
);

const isFreeText = computed(() => {
  if (
    !Number.isFinite(scheduledAtTimestamp.value) ||
    !messageWindow.value.closes_at
  ) {
    return false;
  }

  return (
    scheduledAtTimestamp.value <
    new Date(messageWindow.value.closes_at).getTime()
  );
});

const toLocalDateTimeValue = value => {
  const date = new Date(value);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const minimumScheduledAt = computed(() =>
  toLocalDateTimeValue(new Date(clock.value + 60000))
);

const canSubmit = computed(() => {
  if (!currentChat.value?.can_manage_scheduled_message) return false;
  if (
    !Number.isFinite(scheduledAtTimestamp.value) ||
    scheduledAtTimestamp.value <= clock.value
  ) {
    return false;
  }

  if (isFreeText.value) return Boolean(content.value.trim());

  return Boolean(
    selectedTemplate.value &&
      templateParserRef.value &&
      !templateParserRef.value.isFormInvalid &&
      !templateParserRef.value.v$?.$invalid
  );
});

const contentExplanation = computed(() => {
  if (isFreeText.value) {
    return t('CONVERSATION.SCHEDULED_MESSAGE.FREE_TEXT_EXPLANATION');
  }

  return t('CONVERSATION.SCHEDULED_MESSAGE.TEMPLATE_EXPLANATION');
});

const title = computed(() =>
  isEditing.value
    ? t('CONVERSATION.SCHEDULED_MESSAGE.TITLE_EDIT')
    : t('CONVERSATION.SCHEDULED_MESSAGE.TITLE_CREATE')
);

const confirmLabel = computed(() =>
  isEditing.value
    ? t('CONVERSATION.SCHEDULED_MESSAGE.CONFIRM_EDIT')
    : t('CONVERSATION.SCHEDULED_MESSAGE.CONFIRM')
);

const findSavedTemplate = templateParams => {
  if (!templateParams) return null;

  return (
    availableTemplates.value.find(template => {
      const sameIdentity =
        template.name === templateParams.name &&
        template.language === templateParams.language;
      const sameNamespace =
        !templateParams.namespace ||
        template.namespace === templateParams.namespace;
      return sameIdentity && sameNamespace;
    }) || null
  );
};

const resetForm = () => {
  const pending = scheduledMessage.value;
  editingMessageId.value = pending?.status === 'pending' ? pending.id : null;
  editingLockVersion.value =
    pending?.status === 'pending' ? pending.lock_version : null;
  scheduledAt.value = pending?.scheduled_at
    ? toLocalDateTimeValue(pending.scheduled_at)
    : toLocalDateTimeValue(new Date(Date.now() + 5 * 60000));
  content.value = pending?.content_type === 'text' ? pending.content : '';
  selectedTemplate.value =
    pending?.content_type === 'template'
      ? findSavedTemplate(pending.template_params)
      : null;
  initialProcessedParams.value =
    pending?.content_type === 'template'
      ? pending.template_params?.processed_params
      : undefined;
};

const startClock = () => {
  clearInterval(clockInterval);
  clock.value = Date.now();
  windowLoadedAt.value = clock.value;
  clockInterval = setInterval(() => {
    clock.value = Date.now();
  }, 60000);
};

const close = () => {
  clearInterval(clockInterval);
  dialogRef.value?.close();
};

const handleDialogClose = () => clearInterval(clockInterval);

watch(
  () => messageWindow.value.evaluated_at,
  () => {
    const now = Date.now();
    clock.value = now;
    windowLoadedAt.value = now;
  }
);

const open = async () => {
  try {
    await store.dispatch('fetchScheduledMessage', conversationId.value);
    resetForm();
    startClock();
    dialogRef.value?.open();
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.LOAD_ERROR'));
  }
};

const persistScheduledMessage = async scheduledMessagePayload => {
  isSubmitting.value = true;
  try {
    const action = isEditing.value
      ? 'updateScheduledMessage'
      : 'createScheduledMessage';
    const payload = {
      conversationId: conversationId.value,
      scheduledMessage: scheduledMessagePayload,
    };
    if (isEditing.value) {
      payload.scheduledMessageId = editingMessageId.value;
      payload.scheduledMessage.lock_version = editingLockVersion.value;
    }

    await store.dispatch(action, payload);
    const successMessage = isEditing.value
      ? t('CONVERSATION.SCHEDULED_MESSAGE.EDIT_SUCCESS')
      : t('CONVERSATION.SCHEDULED_MESSAGE.CREATE_SUCCESS');
    useAlert(successMessage);
    close();
  } catch (error) {
    if (error.response?.status === 409) {
      resetForm();
      useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.CONFLICT'));
      return;
    }

    const fallbackError = isEditing.value
      ? t('CONVERSATION.SCHEDULED_MESSAGE.EDIT_ERROR')
      : t('CONVERSATION.SCHEDULED_MESSAGE.CREATE_ERROR');
    useAlert(error.response?.data?.error || fallbackError);
  } finally {
    isSubmitting.value = false;
  }
};

const createTemplateScheduledMessage = ({ templateParams, mediaBlobId }) =>
  persistScheduledMessage({
    scheduled_at: new Date(scheduledAt.value).toISOString(),
    content_type: 'template',
    template_params: templateParams,
    ...(mediaBlobId ? { header_media: mediaBlobId } : {}),
  });

const createScheduledMessage = () => {
  if (!canSubmit.value || isSubmitting.value) return;

  if (!isFreeText.value) {
    templateParserRef.value?.sendMessage();
    return;
  }

  persistScheduledMessage({
    scheduled_at: new Date(scheduledAt.value).toISOString(),
    content_type: 'text',
    content: content.value.trim(),
  });
};

const selectTemplate = template => {
  selectedTemplate.value = template;
  initialProcessedParams.value = undefined;
};

const resetTemplate = () => {
  selectedTemplate.value = null;
  initialProcessedParams.value = undefined;
};

onBeforeUnmount(() => clearInterval(clockInterval));

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    width="lg"
    @confirm="createScheduledMessage"
    @close="handleDialogClose"
  >
    <template #description>
      <div class="flex flex-col gap-2 text-sm text-n-slate-11">
        <p
          data-testid="scheduled-message-timezone"
          class="mb-0 text-xs text-n-slate-10"
        >
          {{
            t('CONVERSATION.SCHEDULED_MESSAGE.TIMEZONE', { timezone: timeZone })
          }}
        </p>
        <div
          data-testid="scheduled-message-window"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2"
        >
          <template v-if="formattedWindowClose">
            <p class="mb-0 text-n-slate-12">
              {{
                t('CONVERSATION.SCHEDULED_MESSAGE.WINDOW_CLOSES_AT', {
                  time: formattedWindowClose,
                })
              }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{
                t('CONVERSATION.SCHEDULED_MESSAGE.WINDOW_REMAINING', {
                  hours: remainingTime.hours,
                  minutes: remainingTime.minutes,
                })
              }}
            </p>
          </template>
          <p v-else class="mb-0 text-n-slate-12">
            {{ t('CONVERSATION.SCHEDULED_MESSAGE.WINDOW_UNAVAILABLE') }}
          </p>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <Input
        v-model="scheduledAt"
        type="datetime-local"
        :label="t('CONVERSATION.SCHEDULED_MESSAGE.SCHEDULED_AT')"
        :min="minimumScheduledAt"
      />

      <div
        class="flex gap-2 p-3 text-sm rounded-lg"
        :class="
          isFreeText
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-amber-3 text-n-amber-11'
        "
      >
        <span
          class="mt-0.5"
          :class="isFreeText ? 'i-ph-check-circle' : 'i-ph-info'"
        />
        <p class="mb-0">
          {{ contentExplanation }}
        </p>
      </div>

      <TextArea
        v-if="isFreeText"
        id="scheduled-message-content"
        v-model="content"
        :label="t('CONVERSATION.SCHEDULED_MESSAGE.MESSAGE_LABEL')"
        :placeholder="t('CONVERSATION.SCHEDULED_MESSAGE.MESSAGE_PLACEHOLDER')"
        :max-length="150000"
        auto-height
        show-character-count
      />
      <div v-else class="flex flex-col gap-3">
        <p
          v-if="isSavedTemplateUnavailable"
          class="mb-0 p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
        >
          {{ t('CONVERSATION.SCHEDULED_MESSAGE.TEMPLATE_EDIT_UNAVAILABLE') }}
        </p>
        <TemplatesPicker
          v-if="!selectedTemplate"
          :inbox-id="inboxId"
          @on-select="selectTemplate"
        />
        <template v-else>
          <button
            type="button"
            class="self-start text-sm font-medium text-n-brand hover:underline"
            @click="resetTemplate"
          >
            {{ t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
          </button>
          <WhatsAppTemplateParser
            ref="templateParserRef"
            :template="selectedTemplate"
            :initial-processed-params="initialProcessedParams"
            media-upload
            @send-message="createTemplateScheduledMessage"
            @back="resetTemplate"
            @reset-template="resetTemplate"
          />
        </template>
      </div>
    </div>
  </Dialog>
</template>
